import { Button, Checkbox, IconButton, TextField } from '@nexelem/components';
import {
  effectiveParallelPairThreshold,
  historicalAssignmentsLookbackMonthsSchema,
  minAvailabilityCoveragePercentSchema,
  minOverlapsForPairPerMonthSchema,
  rankingCriterionHintKeys,
  rankingCriterionLabelKeys,
} from './autoAssignmentConfig';
import { FormControlLabel } from '@nexelem/deprecated-theme';
import { Trans } from 'react-i18next';
import { useWatch } from 'react-hook-form';
import { useCustomTranslation } from '../../../../../hooks';
import { HistoricalParallelMachinePairsPreview } from './HistoricalParallelMachinePairsPreview';
import { useAutoAssignmentConfigStore, useResolvedPolicyConfig } from './useAutoAssignmentConfigStore';
import { AssignmentFormValues } from './useAssignWorkersSubmit';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp } from '@fortawesome/pro-light-svg-icons';

export const AssignmentConfigTab = () => {
  const { tWrapper } = useCustomTranslation('products.productionPlan.autoAssigment');
  const machineAreaId: number | undefined = useWatch<AssignmentFormValues, 'queueAreaId'>({ name: 'queueAreaId' });
  const { policyConfig, serverDefaults } = useResolvedPolicyConfig();
  const rankingConfig = useAutoAssignmentConfigStore((state) => state.rankingConfig);
  const updatePolicyConfig = useAutoAssignmentConfigStore((state) => state.updatePolicyConfig);
  const moveRankingCriterion = useAutoAssignmentConfigStore((state) => state.moveRankingCriterion);
  const toggleRankingCriterion = useAutoAssignmentConfigStore((state) => state.toggleRankingCriterion);
  const resetConfig = useAutoAssignmentConfigStore((state) => state.resetConfig);

  return (
    <div className="flex flex-col">
      <section className="mb-8 flex flex-col">
        <h3 className="mb-1 text-s18-sb">{tWrapper('policy.title')}</h3>
        <p className="text-s16 text-text-secondary-darker">
          <Trans components={{ strong: <strong /> }}>{tWrapper('policy.description')}</Trans>
        </p>
        <FormControlLabel
          className="my-6 ml-2"
          label={<span className="ml-2 text-s16">{tWrapper('policy.requireHistoricalAssignment')}</span>}
          control={
            <Checkbox
              checked={policyConfig.requireHistoricalAssignment}
              onChange={(event) => updatePolicyConfig({ requireHistoricalAssignment: event.target.checked })}
            />
          }
        />
        <TextField
          required
          className="[&_input]:max-w-40"
          label={tWrapper('policy.historicalAssignmentsLookbackMonths')}
          type="number"
          value={String(policyConfig.historicalAssignmentsLookbackMonths)}
          helperText={tWrapper('policy.historicalAssignmentsLookbackMonthsHint', {
            defaultMonths: serverDefaults.historicalAssignmentsLookbackMonths,
          })}
          onChange={(value) => {
            const parsed = historicalAssignmentsLookbackMonthsSchema.safeParse(Number(value));
            if (!parsed.success) {
              return;
            }
            updatePolicyConfig({ historicalAssignmentsLookbackMonths: parsed.data });
          }}
        />
      </section>

      <section className="mb-8 flex flex-col">
        <h3 className="mb-1 text-s18-sb">{tWrapper('policy.parallelPairs.title')}</h3>
        <p className="mb-4 text-s16 text-text-secondary">{tWrapper('policy.parallelPairs.description')}</p>
        <TextField
          required
          className="mb-4 [&_input]:max-w-40"
          label={tWrapper('policy.parallelPairs.minOverlapsForPairPerMonth')}
          type="number"
          value={String(policyConfig.minOverlapsForPairPerMonth)}
          helperText={tWrapper('policy.parallelPairs.minOverlapsForPairPerMonthHint', {
            effectiveThreshold: effectiveParallelPairThreshold(
              policyConfig.minOverlapsForPairPerMonth,
              policyConfig.historicalAssignmentsLookbackMonths,
            ),
            lookbackMonths: policyConfig.historicalAssignmentsLookbackMonths,
          })}
          onChange={(value) => {
            const parsed = minOverlapsForPairPerMonthSchema.safeParse(Number(value));
            if (!parsed.success) {
              return;
            }
            updatePolicyConfig({ minOverlapsForPairPerMonth: parsed.data });
          }}
        />
        <HistoricalParallelMachinePairsPreview
          lookbackMonths={policyConfig.historicalAssignmentsLookbackMonths}
          machineAreaId={machineAreaId}
          minOverlapsForPairPerMonth={policyConfig.minOverlapsForPairPerMonth}
        />
        <TextField
          required
          className="mt-4 [&_input]:max-w-40"
          label={tWrapper('policy.minAvailabilityCoveragePercent')}
          type="number"
          value={String(policyConfig.minAvailabilityCoveragePercent)}
          helperText={tWrapper('policy.minAvailabilityCoveragePercentHint', {
            defaultPercent: serverDefaults.minAvailabilityCoveragePercent,
          })}
          onChange={(value) => {
            const parsed = minAvailabilityCoveragePercentSchema.safeParse(Number(value));
            if (!parsed.success) {
              return;
            }
            updatePolicyConfig({ minAvailabilityCoveragePercent: parsed.data });
          }}
        />
      </section>

      <section className="flex flex-col">
        <h3 className="mb-1 text-s18-sb">{tWrapper('ranking.title')}</h3>
        <p className="mb-6 text-s16 text-text-secondary-darker">{tWrapper('ranking.description')}</p>

        <div className="flex flex-col gap-3">
          {rankingConfig.criteria.map((entry, index) => (
            <div
              className="flex items-center rounded-xs border border-border-container bg-surface-container-lighter p-3"
              key={entry.criterion}
            >
              <span className="text-t16-sb text-text-secondary">{index + 1}.</span>
              <Checkbox
                aria-label={tWrapper(rankingCriterionLabelKeys[entry.criterion])}
                checked={entry.enabled}
                className="ml-4"
                onChange={() => toggleRankingCriterion(index)}
              />
              <div className="mx-4 flex flex-col">
                <p className="text-s16-sb">{tWrapper(rankingCriterionLabelKeys[entry.criterion])}</p>
                <p className="text-s16 text-text-secondary">{tWrapper(rankingCriterionHintKeys[entry.criterion])}</p>
              </div>

              <div className="ml-auto flex flex-col">
                {index !== 0 && (
                  <IconButton
                    aria-label={tWrapper('ranking.moveUp')}
                    Icon={<FontAwesomeIcon icon={faArrowUp} />}
                    size="small"
                    onClick={() => moveRankingCriterion(index, -1)}
                  />
                )}
                {index !== rankingConfig.criteria.length - 1 && (
                  <IconButton
                    aria-label={tWrapper('ranking.moveDown')}
                    Icon={<FontAwesomeIcon icon={faArrowDown} />}
                    size="small"
                    onClick={() => moveRankingCriterion(index, 1)}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="mt-8 flex justify-end">
        <Button type="button" variant="outlined" onClick={resetConfig}>
          {tWrapper('resetDefaults')}
        </Button>
      </div>
    </div>
  );
};
